import * as React from "react"
import { graphql, PageProps } from "gatsby"
import { StaticImage, GatsbyImage } from "gatsby-plugin-image"
import { InView } from "react-intersection-observer"

import { ArrayElement } from "../utils/type"

import SEO from "../components/seo"
import Layout from "../components/layout"
import Breadcrumb from "../components/breadcrumb"
import Heading from "../components/heading"
import Space from "../components/space"
import Text from "../components/text"
import Divider from "../components/divider"
import Link from "../components/link"
import ButtonFill from "../components/button-fill"
// import AdsenseAuto from "../components/adsense-auto"
// import AdsenseBanner from "../components/adsense-banner"
import ModalRegistration from "../components/modal-registration"
import FooterRegistration from "../components/footer-registration"
import ArticleHeadings from "../components/article-headings"
import ReadMore from "../components/read-more"

import {
  getArticleDescription,
  ogDescription,
  htmlText,
  sectionsHeadings,
  headingSummary,
} from "../utils/article"
import { onViewArticle, onClickArticleContentAd } from "../utils/hook"
import { getHost } from "../utils/url"
import { genArticleBreadcrumbs, genJsonLdArticle } from "../utils/seo"
import { formatDate, formatISO } from "../utils/date"
import ArticleGridArticles from "../components/article-grid-articles"
import Categories from "../components/categories"
import SocialShare from "../components/social-share"
import ArticleLayout from "../components/article-layout"
import ArticleAd from "../components/article-ad"
import ArticleLinkable from "../components/article-linkable"
import Video from "../components/video"

type State = {
  showRegistrationModal: boolean
}

type PageContext = {
  article: ArrayElement<Queries.CreateDetailReportQuery["allContentfulArticleReport"]["nodes"]>
  isPremiumArticle: boolean
  mainTextLen: number
}

class ArticleReportTemplate extends React.Component<
  PageProps<Queries.TemplateArticleReportQuery, PageContext>,
  State
> {
  constructor(props: any) {
    super(props)
    this.state = { showRegistrationModal: false }
  }
  componentDidMount() {
    const article = this.props.pageContext.article
    onViewArticle({ location: this.props.location, id: article.slug! })
  }
  render() {
    const {
      isPremiumArticle,
      mainTextLen,
    } = this.props.pageContext

    const article = this.props.pageContext.article
    const relatedArticles = this.props.data.relatedContentfulArticle.nodes
    const relatedArticleRecipes =
      this.props.data.relatedContentfulArticleRecipe.nodes
    const relatedArticleReports =
      this.props.data.relatedContentfulArticleReport.nodes
    const newArticles = this.props.data.newContentfulArticle.nodes
    const newArticleRecipes = this.props.data.newContentfulArticleRecipe.nodes
    const newArticleReports = this.props.data.newContentfulArticleReport.nodes
    const newArticleInterviews =
      this.props.data.newContentfulArticleInterview.nodes
    const rankingArticles = this.props.data.rankingContentfulArticle.nodes
    const rankingArticleRecipes =
      this.props.data.rankingContentfulArticleRecipe.nodes
    const rankingArticleReports =
      this.props.data.rankingContentfulArticleReport.nodes
    const rankingArticleInterviews =
      this.props.data.rankingContentfulArticleInterview.nodes

    const utmSource = "media"
    const utmMedium = "column"
    const utmCampaign = article.slug!
    const utmContent = ""

    const breadcrumbs = genArticleBreadcrumbs({
      article: {
        slug: article.slug!,
        title: article.title!,
        typename: article.__typename,
        category: article.category,
      },
    })

    const headings = sectionsHeadings(article)
    const headingComponent = (heading: string) => {
      const index = headings.findIndex(v => v === heading)
      return (
        <div>
          <Divider />
          <Space sizeY="8" lgSizeY="10" />
          <h2 id={`Heading-${index}`}>
            <div className="flex space-x-2">
              <Divider color="blue-800" direction="vertical" />
              <Text fontSize="lg" lgFontSize="xl" fontBold={true}>
                {heading}
              </Text>
            </div>
          </h2>
        </div>
      )
    }

    return (
      <Layout utmSource="media" utmMedium="column" utmCampaign={article.slug!} utmContent="">
        <Space sizeY="3" lgSizeY="6" />
        <ArticleLayout>
          <Breadcrumb list={breadcrumbs} />
          <Space sizeY="6" lgSizeY="12" />
          <div className="max-w-2xl mx-auto">
            <GatsbyImage
              image={article.coverImage!.gatsbyImageData!}
              alt={article.coverImage!.title!}
              className="w-full rounded-lg"
              imgClassName="w-full rounded-lg"
            />
            <Space sizeY="4" lgSizeY="6" />
            <Heading fontSize="xl" lgFontSize="2xl">
              {article.title}
            </Heading>
            <Space sizeY="4" lgSizeY="8" />
            <>
              {/* NOTE わたしプロローグの記事だけPRを手動でいれる */}
              {article.slug === "premenstrual-supplement" && (
                <>
                  <Text fontSize="sm" lgFontSize="base">【PR】※わたしプロローグの情報提供元：アサヒグループ食品株式会社</Text>
                  <Space sizeY="4" lgSizeY="6" />
                </>
              )}
              <Text fontSize="sm" lgFontSize="base" classes="whitespace-pre-wrap">
                {getArticleDescription(article)}
              </Text>
            </>
            <Space sizeY="4" lgSizeY="6" />
            <div className="flex items-center space-x-4">
              <Text fontSize="xs" lgFontSize="sm" fontAvenir={true}>
                公開日：
                <time dateTime={formatISO(article.createdAt!)}>
                  {formatDate(article.createdAt!)}
                </time>
              </Text>
              <Text fontSize="xs" lgFontSize="sm" fontAvenir={true}>
                更新日：
                <time dateTime={formatISO(article.updatedAt!)}>
                  {formatDate(article.updatedAt!)}
                </time>
              </Text>
            </div>
            {/* <Space sizeY="8" lgSizeY="10" />
              <AdsenseAuto
                dataAdClient="ca-pub-4173652456270099"
                dataAdSlot="3196912974"
                dataFullWidthResponsive="true"
              /> */}
            {headings.length !== 0 && (
              <div>
                <Space sizeY="8" lgSizeY="10" />
                <ArticleHeadings headings={headings} isPremiumArticle={isPremiumArticle} />
              </div>
            )}

            {isPremiumArticle ? (
              <>
                <ReadMore
                  mainTextLen={mainTextLen}
                  nagativeMarginTop={0 < headings.length}
                  utmSource={utmSource}
                  utmMedium={utmMedium}
                  utmCampaign={utmCampaign}
                  utmContent={utmContent}
                />
              </>
            ) : (
              <>
                <Space sizeY="8" lgSizeY="10" />
                <div>
                  {article.sections!.map((section, i) => {
                    return (
                      <div key={`article-sections-${i}`}>
                        {headingComponent(section!.title!)}
                        {section!.contents!.map((content, ii) => {
                          return (
                            <div key={`section-content-${i}-${ii}`}>
                              {content!.contentTitle && (
                                <div>
                                  <Space sizeY="8" lgSizeY="10" />
                                  <div className="flex space-x-1.5 lg:space-x-2">
                                    <div className="mt-1">
                                      <div className="border-3 border-blue-800 rounded-full h-4 w-4" />
                                    </div>
                                    <div>
                                      <Text
                                        fontSize="base"
                                        lgFontSize="lg"
                                        fontBold={true}
                                      >
                                        {content!.contentTitle}
                                      </Text>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {content!.contentText && (
                                <div>
                                  <Space sizeY="6" lgSizeY="8" />
                                  <Text fontSize="sm" lgFontSize="base">
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: htmlText(
                                          content!.contentText!.childMarkdownRemark!
                                            .html!
                                        ),
                                      }}
                                    />
                                  </Text>
                                </div>
                              )}
                              {content!.contentImage && (
                                <div>
                                  <Space sizeY="6" lgSizeY="8" />
                                  <ArticleLinkable url={content!.contentImageUrl}>
                                    {content!.contentImage!.mimeType === "video/quicktime"
                                      ? <Video
                                        src={content!.contentImage!.url!}
                                        videoClass="w-1/2 lg:w-1/3"
                                      />
                                      : <GatsbyImage
                                        image={content!.contentImage!.gatsbyImageData!}
                                        alt={content!.contentImage!.title!}
                                        className="w-full rounded-lg"
                                        imgClassName="w-full rounded-lg"
                                      />
                                    }
                                  </ArticleLinkable>
                                </div>
                              )}
                              {(content!.contentAd || content!.contentAdImage) && (
                                <ArticleAd article={article} content={content} />
                              )}
                              {content!.contentRawHtml && (
                                <div>
                                  <Space sizeY="6" lgSizeY="8" />
                                  <div
                                    className="flex items-center justify-center"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        content!.contentRawHtml!.contentRawHtml!,
                                    }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          )
                        })}
                        <Space sizeY="8" lgSizeY="10" />
                      </div>
                    )
                  })}
                </div>
                {article.summary && (
                  <div>
                    {headingComponent(headingSummary)}
                    <Space sizeY="6" lgSizeY="8" />
                    <Text>
                      <div
                        className="article-summary"
                        dangerouslySetInnerHTML={{
                          __html: article.summary.childMarkdownRemark!.html!,
                        }}
                      />
                    </Text>
                  </div>
                )}
                {/* <Space sizeY="8" lgSizeY="10" />
                <AdsenseAuto
                  dataAdClient="ca-pub-4173652456270099"
                  dataAdSlot="7338218400"
                  dataFullWidthResponsive="true"
                /> */}
                <InView
                  className="lg:hidden"
                  as="div"
                  triggerOnce
                  onChange={(inView, entry) => {
                    this.setState({ showRegistrationModal: inView })
                  }}
                />
                <Space sizeY="4" lgSizeY="8" />
                <Text fontSize="lg" lgFontSize="xl" fontBold={true}>
                  この記事を書いた人
                </Text>
                <Space sizeY="2" lgSizeY="4" />
                <div className="py-2 lg:py-4">
                  <div className="flex">
                    <div>
                      <StaticImage
                        src="../images/icon.png"
                        alt="ペアケア編集部"
                        placeholder="none"
                        width={96}
                        className="rounded-full w-16 h-16 lg:w-24 lg:h-24"
                        imgClassName="rounded-full w-16 h-16 lg:w-24 lg:h-24"
                      />
                    </div>
                    <div className="ml-3 lg:ml-6">
                      <Text fontBold={true}>ペアケア編集部</Text>
                      <Space sizeY="1" lgSizeY="2" />
                      <Text fontSize="sm" lgFontSize="base">
                        LINEに友だち追加するだけで生理日管理ができる「
                        <Link href="/" classes="text-blue-800 font-bold">
                          ペアケア
                        </Link>
                        」の運営と生理や女性の身体・健康にまつわる知識や情報を発信しています。
                      </Text>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </ArticleLayout>
        <Space sizeY="6" lgSizeY="12" />
        <ArticleLayout classes="bg-gray-100 py-5 max-w-2xl mx-auto">
          <SocialShare
            url={`/media/column/report/${article.slug}/`}
            title={article.title!}
          />
        </ArticleLayout>
        <>
          {(relatedArticles.length > 0 ||
            relatedArticleRecipes.length > 0 ||
            relatedArticleReports.length > 0) && (
            <>
              <Space sizeY="12" lgSizeY="20" />
              <ArticleLayout>
                <ArticleGridArticles
                  title="関連コラム"
                  articles={relatedArticles}
                  articleRecipes={relatedArticleRecipes}
                  articleReports={relatedArticleReports}
                />
              </ArticleLayout>
            </>
          )}
        </>
        <>
          {(newArticles.length > 0 ||
            newArticleRecipes.length > 0 ||
            newArticleReports.length > 0 ||
            newArticleInterviews.length > 0) && (
            <>
              <Space sizeY="12" lgSizeY="20" />
              <ArticleLayout classes="bg-gray-100 py-10 lg:py-16">
                <ArticleGridArticles
                  title="新着コラム"
                  articles={newArticles}
                  articleRecipes={newArticleRecipes}
                  articleReports={newArticleReports}
                  articleInterviews={newArticleInterviews}
                />
              </ArticleLayout>
            </>
          )}
        </>
        <>
          {(rankingArticles.length > 0 ||
            rankingArticleRecipes.length > 0 ||
            rankingArticleReports.length > 0 ||
            rankingArticleInterviews.length > 0) && (
            <>
              <Space sizeY="12" lgSizeY="20" />
              <ArticleLayout>
                <ArticleGridArticles
                  title="ランキング"
                  displayIndex={true}
                  articles={rankingArticles}
                  articleRecipes={rankingArticleRecipes}
                  articleReports={rankingArticleReports}
                  articleInterviews={rankingArticleInterviews}
                />
              </ArticleLayout>
            </>
          )}
        </>
        <Space sizeY="12" lgSizeY="20" />
        <ArticleLayout classes="bg-gray-100 py-12 lg:py-16">
          <Categories />
        </ArticleLayout>
        {/* <Space sizeY="24" /> */}
        <div className="lg:hidden">
          <div className="fixed left-0 bottom-0 w-full z-10">
            <FooterRegistration
              utmSource="media"
              utmMedium="column"
              utmCampaign={article.slug!}
              utmContent=""
            />
            <Space sizeY="5" />
            {/* <AdsenseBanner
              dataAdClient="ca-pub-4173652456270099"
              dataAdSlot="7769869768"
            /> */}
          </div>
          {this.state.showRegistrationModal && (
            <ModalRegistration
              utmSource="media"
              utmMedium="column"
              utmCampaign={article.slug!}
              utmContent=""
            />
          )}
        </div>
      </Layout>
    )
  }
}

export default ArticleReportTemplate

export function Head(props: PageProps<Queries.TemplateArticleReportQuery, PageContext>) {
  const article = props.pageContext.article
  const site = props.data.site!
  const jsonLdArticle = genJsonLdArticle({
    siteName: site.siteMetadata!.siteName!,
    siteURL: site.siteMetadata!.siteUrl!,
    article: {
      title: article.title!,
      imageURL: `${site.siteMetadata!.siteUrl!}${article.coverImage!.resize!
        .src!}`,
      datePublished: formatISO(article.createdAt!),
      dateModified: formatISO(article.updatedAt!),
      authors: [
        {
          name: "ペアケア編集部",
          url: "",
          jobTitle: "",
          description:
            "LINEに友だち追加するだけで生理日管理ができる「ペアケア」の運営と生理や女性の身体・健康にまつわる知識や情報を発信しています。",
          imageURL: "",
        },
      ],
    },
  })
  return (
    <>
      <SEO
        title={`${article.title}`}
        description={ogDescription(article)}
        url={`/media/column/report/${article.slug}/`}
        imageURL={article.coverImage!.resize!.src!}
      >
        <script type="application/ld+json">
          {JSON.stringify(jsonLdArticle)}
        </script>
      </SEO>
    </>
  )
}

export const pageQuery = graphql`
  fragment frArticleConversation on ContentfulArticle {
    premiumStatus
    slug
    title
    description
    descriptionLong {
      descriptionLong
    }
    coverImage {
      title
      gatsbyImageData(
        layout: FULL_WIDTH
        placeholder: BLURRED
        width: 424
        height: 239
      )
    }
    createdAt
    updatedAt
  }
  fragment frArticleReport on ContentfulArticleReport {
    premiumStatus
    slug
    title
    description
    descriptionLong {
      descriptionLong
    }
    coverImage {
      title
      gatsbyImageData(
        layout: FULL_WIDTH
        placeholder: BLURRED
        width: 424
        height: 239
      )
    }
    createdAt
    updatedAt
  }
  fragment frArticleRecipe on ContentfulArticleRecipe {
    premiumStatus
    slug
    title
    description
    descriptionLong {
      descriptionLong
    }
    coverImage {
      title
      gatsbyImageData(
        layout: FULL_WIDTH
        placeholder: BLURRED
        width: 424
        height: 239
      )
    }
    createdAt
    updatedAt
  }
  fragment frArticleInterview on ContentfulArticleInterview {
    premiumStatus
    slug
    title
    description
    descriptionLong {
      descriptionLong
    }
    coverImage {
      title
      gatsbyImageData(
        layout: FULL_WIDTH
        placeholder: BLURRED
        width: 424
        height: 239
      )
    }
    createdAt
    updatedAt
  }

  query TemplateArticleReport(
    $relatedArticleIDs: [String]
    $relatedArticleRecipeIDs: [String]
    $relatedArticleReportIDs: [String]
    $newArticleIDs: [String]
    $newArticleRecipeIDs: [String]
    $newArticleReportIDs: [String]
    $newArticleInterviewIDs: [String]
    $rankingArticleIDs: [String]
    $rankingArticleRecipeIDs: [String]
    $rankingArticleReportIDs: [String]
    $rankingArticleInterviewIDs: [String]
  ) {
    site {
      siteMetadata {
        siteName
        siteUrl
      }
    }
    relatedContentfulArticle: allContentfulArticle(
      filter: {
        contentful_id: { in: $relatedArticleIDs }
      }
    ) {
      nodes {
        ...frArticleConversation
      }
    }
    relatedContentfulArticleRecipe: allContentfulArticleRecipe(
      filter: {
        contentful_id: { in: $relatedArticleRecipeIDs }
      }
    ) {
      nodes {
        ...frArticleRecipe
      }
    }
    relatedContentfulArticleReport: allContentfulArticleReport(
      filter: {
        contentful_id: { in: $relatedArticleReportIDs }
      }
    ) {
      nodes {
        ...frArticleReport
      }
    }
    newContentfulArticle: allContentfulArticle(
      filter: {
        contentful_id: { in: $newArticleIDs }
      }
    ) {
      nodes {
        ...frArticleConversation
      }
    }
    newContentfulArticleRecipe: allContentfulArticleRecipe(
      filter: {
        contentful_id: { in: $newArticleRecipeIDs }
      }
    ) {
      nodes {
        ...frArticleRecipe
      }
    }
    newContentfulArticleReport: allContentfulArticleReport(
      filter: {
        contentful_id: { in: $newArticleReportIDs }
      }
    ) {
      nodes {
        ...frArticleReport
      }
    }
    newContentfulArticleInterview: allContentfulArticleInterview(
      filter: {
        contentful_id: { in: $newArticleInterviewIDs }
      }
    ) {
      nodes {
        ...frArticleInterview
      }
    }
    rankingContentfulArticle: allContentfulArticle(
      filter: {
        contentful_id: { in: $rankingArticleIDs }
      }
    ) {
      nodes {
        ...frArticleConversation
      }
    }
    rankingContentfulArticleRecipe: allContentfulArticleRecipe(
      filter: {
        contentful_id: { in: $rankingArticleRecipeIDs }
      }
    ) {
      nodes {
        ...frArticleRecipe
      }
    }
    rankingContentfulArticleReport: allContentfulArticleReport(
      filter: {
        contentful_id: { in: $rankingArticleReportIDs }
      }
    ) {
      nodes {
        ...frArticleReport
      }
    }
    rankingContentfulArticleInterview: allContentfulArticleInterview(
      filter: {
        contentful_id: { in: $rankingArticleInterviewIDs }
      }
    ) {
      nodes {
        ...frArticleInterview
      }
    }
  }
`
